/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';

import { AvatarLarge, InlineTextButton, NamedLink, NotificationBadge, Logo } from '../../../components';
import IconPlusSetting from './IconPlus/IconPlusSettings';
import IconPlusInbox from './IconPlus/IconPlusInbox';


import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
    currentTab,
    customButton,
  } = props;

  const [isSettingsOpen, setIsSettingsOpen] = useState(ACCOUNT_SETTINGS_PAGES.includes(currentPage));
  const [isInboxOpen, setIsInboxOpen] = useState(currentPage === 'InboxPage' && currentTab);

  const user = ensureCurrentUser(currentUser);

  if (!isAuthenticated) {

    return (
      <div className={css.root}>
        <div className={css.content}>
          {customButton}
          <NamedLink className={css.links} name="SearchPage">
            <FormattedMessage id="TopbarMobileMenu.searchLink" />
          </NamedLink>
          <NamedLink className={css.links} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
          <NamedLink className={css.links} name="LoginPage">
            <FormattedMessage id="TopbarMobileMenu.loginLink" />
          </NamedLink>
          <div className={css.divider} />
          <NamedLink className={css.links} name="HowToRotatePage" >
            How to ROTATE
          </NamedLink>
          <NamedLink className={css.links} name="WhyRotatePage">
            <FormattedMessage id="TopbarMobileMenu.whyRotateLink" />
          </NamedLink>
          <NamedLink className={css.links} name="AboutPage" >
            Our Story
          </NamedLink>
          <NamedLink className={css.links} name="SignupPage">
            <FormattedMessage id="TopbarMobileMenu.signupLink" />
          </NamedLink>
          <NamedLink className={css.links} name="TermsOfServicePage" >
            T&Cs
          </NamedLink>
          <NamedLink className={css.links} name="PrivacyPolicyPage" >
            Privacy Policy
          </NamedLink>
          <a href="mailto:hq@rotatenow.co.nz" className={css.links}>
            Contact Us
          </a>
        </div>
        <div className={css.footer}>
          <NamedLink name="LandingPage" className={css.logoLink}>
            <Logo className={css.logoLinkImage} format="desktopFooter" darkBackground={true} />
          </NamedLink>
          <p className={css.description}>
            <FormattedMessage id="Footer.organizationDescription" />
          </p>
          <NamedLink name="LandingPage" className={css.copyrightLink}>
            <FormattedMessage id="Footer.copyright" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      {/* <AvatarLarge className={css.avatar} user={currentUser} /> */}
      <div className={css.content}>
        {customButton}
        <NamedLink className={css.links} name="SearchPage">
          <FormattedMessage id="TopbarMobileMenu.searchLink" />
        </NamedLink>
        <NamedLink className={css.links} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
        <div className={css.divider} />
        <NamedLink
          className={classNames(css.links, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.links, currentPageClass('FavListingsPage'))}
          name="FavListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.FavListingsPage" />
        </NamedLink>

        {/* <div className={css.dropdownButton} onClick={() => setIsInboxOpen(!isInboxOpen)}>
          <span className={css.links}>
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </span>
          <IconPlusInbox isInboxOpen={isInboxOpen} />
        </div> */}

        {/* <div
          className={classNames(css.dropdown, { [css.isOpen]: isInboxOpen })}
        > */}
        <NamedLink
          className={classNames(css.links, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'orders' })}
          name="InboxPage" params={{ tab: 'orders' }}
        >
          <FormattedMessage id="InboxPage.ordersTabTitle" />
        </NamedLink>
        <NamedLink
          className={classNames(css.links, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'sales' })}
          name="InboxPage" params={{ tab: 'sales' }}
        >
          <FormattedMessage id="InboxPage.salesTabTitle" />
        </NamedLink>
        {/* </div> */}

        <NamedLink
          className={classNames(css.links, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>

        <div className={css.dropdownButton} onClick={() => setIsSettingsOpen(!isSettingsOpen)}>
          <span className={css.links}>
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </span>
          <IconPlusSetting isSettingsOpen={isSettingsOpen} />
        </div>

        <div
          className={classNames(css.dropdown, { [css.isOpen]: isSettingsOpen })}
        >
          <NamedLink
            className={classNames(css.links, currentPageClass('ContactDetailsPage'))}
            name="ContactDetailsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.contactDetailsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.links, currentPageClass('PasswordChangePage'))}
            name="PasswordChangePage">
            <FormattedMessage id="TopbarMobileMenu.passwordLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.links, currentPageClass('PaymentMethodsPage'))}
            name="PaymentMethodsPage">
            <FormattedMessage id="TopbarMobileMenu.payoutDetailsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.links, currentPageClass('StripePayoutPage'))}
            name="StripePayoutPage">
            <FormattedMessage id="TopbarMobileMenu.paymentMethodsLink" />
          </NamedLink>
        </div>
        <InlineTextButton className={css.links} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <div className={css.divider} />
        <NamedLink className={css.links} name="HowToRotatePage" >
          How to ROTATE
        </NamedLink>
        <NamedLink className={css.links} name="WhyRotatePage">
          Why ROTATE
        </NamedLink>
        <NamedLink className={css.links} name="AboutPage" >
          Our Story
        </NamedLink>
        <NamedLink className={css.links} name="SignupPage">
          <FormattedMessage id="TopbarMobileMenu.signupLink" />
        </NamedLink>
        <NamedLink className={css.links} name="TermsOfServicePage" >
          T&Cs
        </NamedLink>
        <NamedLink className={css.links} name="PrivacyPolicyPage" >
          Privacy Policy
        </NamedLink>
        <a href="mailto:hq@rotatenow.co.nz" className={css.links}>
          Contact Us
        </a>
      </div>
      <div className={css.footer}>
        <NamedLink name="LandingPage" className={css.logoLink}>
          <Logo format="desktopFooter" darkBackground={true} />
        </NamedLink>
        <p className={css.description}>
          <FormattedMessage id="Footer.organizationDescription" />
        </p>
        <NamedLink name="LandingPage" className={css.copyrightLink}>
          <FormattedMessage id="Footer.copyright" />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
